// src/context/UserContext.js
import { useEffect, useState, createContext } from 'react';
import { auth, firestore } from '../utils/firebase-config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ROLES } from '../constants/roles'; 
import { COLLECTION_NAMES } from '../config/firestore-collections';
import logger from '../utils/logger';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(ROLES.PENDIENTE);
  const [userPermissions, setUserPermissions] = useState([]); // Nuevo estado para permisos
  const [loading, setLoading] = useState(true);

  const fetchUserRoleAndPermissions = async (user) => {
    setUserRole(ROLES.PENDIENTE);
    setUserPermissions([]);
    setLoading(true);
    try {
      // 1. Obtener datos del usuario (rol)
      const userDocRef = doc(firestore, COLLECTION_NAMES.users, user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const roleName = userData.role || ROLES.PENDIENTE;
        setUserRole(roleName);

        // 2. Obtener Permisos del Rol desde la colección 'roles'
        // Buscamos el documento en la colección de roles que coincida con el nombre del rol
        const rolesRef = collection(firestore, COLLECTION_NAMES.roles);
        const q = query(rolesRef, where("name", "==", roleName));
        const roleSnap = await getDocs(q);

        if (!roleSnap.empty) {
          const roleData = roleSnap.docs[0].data();
          setUserPermissions(roleData.permissions || []);
        } else if (roleName === ROLES.SUPER_ADMIN) {
          // Fallback para Super Admin si no estuviere en la DB
          setUserPermissions(['all_access']);
        }
        
        // 3. Añadir permisos personalizados del usuario si existen
        if (userData.customPermissions && Array.isArray(userData.customPermissions)) {
          setUserPermissions(prev => [...new Set([...prev, ...userData.customPermissions])]);
        }

      } else {
        setUserRole(ROLES.PENDIENTE);
      }
    } catch (error) {
      logger.error("Error al obtener el perfil de seguridad:", error);
      setUserRole(ROLES.PENDIENTE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        fetchUserRoleAndPermissions(user);
      } else {
        setUserRole(ROLES.PENDIENTE);
        setUserPermissions([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const hasPermission = (permission) => {
    if (userRole === ROLES.SUPER_ADMIN || userPermissions.includes('all_access')) return true;
    return userPermissions.includes(permission);
  };

  return (
    <UserContext.Provider value={{ 
      currentUser, 
      setCurrentUser, 
      userRole, 
      setUserRole, 
      userPermissions,
      hasPermission, // Exponemos la función de verificación
      loading 
    }}>
      {children}
    </UserContext.Provider>
  );
};